import { useState, useEffect, memo, Fragment, useRef, useContext } from 'react'
import { useForm } from 'react-hook-form'
import {
  Stack,
  Checkbox,
  Button,
  Visible,
  Hidden,
  Inline
} from '@leroy-merlin-br/backyard-react-ui'
import {
  NumberField,
  TextField,
  PasswordField,
  SelectField,
  Popover
} from 'signup/components'
import { useHistory } from 'react-router-dom'
import { useIsLogged } from 'signup/hooks/context/IsLoggedContext.js'
import * as service from 'signup/services'
import * as S from 'signup/pages/SignUp/styled'
import {
  validateError500,
  handlePhoneFormat,
  hasAccountFiscalId,
  birthdateErrorMessage,
  handleErrors
} from 'signup/utils'

import { HeaderContext } from 'reactSite/Header/contexts/HeaderContext'

import * as validator from 'reactUtils/validators/validators'

import DocumentPro from 'lmcv/components/DocumentPro'

import {
  generalNewsletterToggle,
  newsletterEmailToggle,
  newsletterSmsToggle,
  newsletterWhatsAppToggle
} from './newsLetterBuilder'

const FormPF = ({ recaptchaRef }) => {
  const { control, register, errors, setError, handleSubmit, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      fiscalId: '',
      name: '',
      email: '',
      emailConfirm: '',
      phone: '',
      password: '',

      loyalty: {
        occupation: '',
        favoriteStore: '',
        birthdate: ''
      },

      optins: {
        email: false,
        sms: false,
        whatsApp: false
      }
    }
  })

  const history = useHistory()

  const { setIsLogged } = useIsLogged()

  const [joinLoyalty, setjoinLoyalty] = useState(false)
  const [isProfessional, setIsProfessional] = useState(false)
  const [apiErrors, setApiErrors] = useState([])
  const [isRequest, setIsRequest] = useState(false)
  const [classOccupation, setClassOccupation] = useState('')
  const [stores, setStores] = useState([])

  const [newsLetterState, setNewsLetterState] = useState({
    general: 'unchecked',
    email: false,
    sms: false,
    whatsapp: false
  })

  const { userResources } = useContext(HeaderContext)

  const redirectTo = useRef()

  useEffect(() => {
    apiErrors.map(({ code, message, messages }) => {
      return setError(code, {
        type: 'manual',
        message: handleErrors(message, messages)
      })
    })
  }, [apiErrors, setError])

  const handleJoinLoyalty = () => {
    setjoinLoyalty(prev => !prev)
    setIsProfessional(false)
  }

  const handleIsProfessional = () => {
    setIsProfessional(prev => !prev)
  }

  const handleSubmitPF = async value => {
    const submittedDataPF = {
      ...value,
      phone: `+55${value.phone}`
    }

    const recaptchaToken = recaptchaRef.current.executeAsync()

    setIsRequest(true)
    service
      .signup(submittedDataPF, history.location.search, await recaptchaToken)
      .then(({ data }) => {
        setIsLogged(true)

        redirectTo.current = data.redirect

        if (data.needDocumentation) {
          setClassOccupation(submittedDataPF.loyalty?.occupation)
          return
        }

        onSubmitDocument()
      })
      .catch(({ data, status }) => {
        setIsLogged(false)

        recaptchaRef.current.reset()

        const shouldRedirect = validateError500(status)

        if (shouldRedirect) {
          history.push('/erro-interno')
        }

        setApiErrors(data.errors)
      })
      .finally(() => {
        setIsRequest(false)
      })
  }

  const onSubmitDocument = () => {
    history.push({
      pathname: '/sucesso',
      state: { redirect: redirectTo.current }
    })
  }

  const optInsCheckboxes = [
    <Checkbox
      name="optins.email"
      ref={register}
      onClick={() =>
        setNewsLetterState(prevState => newsletterEmailToggle(prevState))
      }
      checked={newsLetterState.email}
    >
      E-mail
    </Checkbox>,

    <Checkbox
      name="optins.sms"
      ref={register}
      onClick={() =>
        setNewsLetterState(prevState => newsletterSmsToggle(prevState))
      }
      checked={newsLetterState.sms}
    >
      SMS
    </Checkbox>,

    <Checkbox
      name="optins.whatsApp"
      ref={register}
      onClick={() =>
        setNewsLetterState(prevState => newsletterWhatsAppToggle(prevState))
      }
      checked={newsLetterState.whatsapp}
    >
      Whatsapp
    </Checkbox>
  ].map(item => item)

  useEffect(() => {
    const fetchStores = async () => {
      const region = userResources.region
      const response = await service.getStores(region)
      const { data } = response

      const storesObject = data.map(item => ({
        value: item.id,
        label: item.name
      }))

      setStores(storesObject)
    }

    if (userResources.region) {
      fetchStores()
    }
  }, [userResources.region])

  return (
    <Fragment>
      <DocumentPro
        open={classOccupation !== ''}
        onCancel={onSubmitDocument}
        onFinish={onSubmitDocument}
        classOccupation={classOccupation}
      />
      <form onSubmit={handleSubmit(handleSubmitPF)} data-cy="signup-form">
        <Stack spacing="mega">
          <NumberField
            name="fiscalId"
            label={
              <Popover
                list={{
                  title: 'Seu CPF será usado para:',
                  items: [
                    'Te identificar',
                    'Acessar sua conta',
                    'Consultar pedidos',
                    'Realizar transações financeiras'
                  ]
                }}
              >
                CPF
              </Popover>
            }
            rules={{
              required: 'Insira seu CPF',
              validate: value => validator.isCpf(value) || 'CPF inválido'
            }}
            control={control}
            isInvalid={Boolean(errors.fiscalId)}
            validationHint={hasAccountFiscalId(errors, 'CPF')}
            format="###.###.###-##"
          />
          <TextField
            name="name"
            label={
              <Popover
                list={{
                  title: 'Seu Nome será usado para:',
                  items: ['Te identificar', 'Saber como podemos te chamar']
                }}
                info="Se você preferir, pode informar seu nome social com sobrenome, ok?"
              >
                Nome completo
              </Popover>
            }
            rules={{
              required: 'Insira seu nome',
              validate: value => validator.isValidName(value) || 'Nome inválido'
            }}
            control={control}
            isInvalid={Boolean(errors.name)}
            validationHint={errors.name?.message}
          />

          <TextField
            name="email"
            type="email"
            label={
              <Popover
                list={{
                  title: 'Seu e-mail será usado para:',
                  items: [
                    'Te identificar',
                    'Acessar sua conta',
                    'Receber sua nota fiscal',
                    'Receber o status do seu pedido'
                  ]
                }}
              >
                E-mail
              </Popover>
            }
            rules={{
              required: 'Insira seu e-mail',
              validate: value => validator.isEmail(value) || 'E-mail inválido'
            }}
            control={control}
            autoComplete="new-password"
            isInvalid={Boolean(errors.email)}
            validationHint={errors.email?.message}
          />

          {Boolean(watch('email')) && (
            <TextField
              name="emailConfirm"
              type="email"
              label="Confirmar e-mail"
              rules={{
                required: 'Insira seu e-mail',
                validate: value =>
                  value === watch('email') || 'Os e-mail precisam ser iguais'
              }}
              onPaste={e => {
                e.preventDefault()

                return false
              }}
              control={control}
              autoComplete="new-password"
              isInvalid={Boolean(errors.emailConfirm)}
              validationHint={errors.emailConfirm?.message}
            />
          )}

          <NumberField
            name="phone"
            label={
              <Popover
                list={{
                  title: 'Seu telefone será usado para:',
                  items: [
                    'Garantir mais segurança para sua conta',
                    'Contato rápido para informar sobre seu pedido',
                    'Envio de novidades e ofertas, se você aceitar :)'
                  ]
                }}
              >
                Telefone
              </Popover>
            }
            rules={{
              required: 'Insira seu Telefone',
              validate: value => validator.isPhone(value) || 'Telefone inválido'
            }}
            control={control}
            isInvalid={Boolean(errors.phone)}
            validationHint={errors.phone?.message}
            format={handlePhoneFormat}
          />

          <PasswordField
            name="password"
            label="Senha"
            rules={{
              required: 'Insira sua senha'
            }}
            control={control}
            autoComplete="new-password"
            isInvalid={Boolean(errors.password)}
            validationHint={errors.password?.message}
          />

          <Checkbox
            name="loyalty.optIn"
            validationHint="Cupons, pontos e vantagens para você!"
            ref={register}
            onChange={handleJoinLoyalty}
          >
            Participar do programa de fidelidade
          </Checkbox>

          {joinLoyalty && (
            <Stack spacing="mega">
              <SelectField
                name="loyalty.favoriteStore"
                label={
                  <Popover
                    list={{
                      title: 'Sua loja favorita será usada para:',
                      items: [
                        'Envio de ofertas e conteúdo exclusivos da loja escolhida'
                      ]
                    }}
                  >
                    Loja favorita
                  </Popover>
                }
                options={stores}
                rules={{
                  required: 'Escolha sua loja favorita'
                }}
                control={control}
                isInvalid={Boolean(errors.loyalty?.favoriteStore)}
                validationHint={errors.loyalty?.favoriteStore?.message}
              />

              <NumberField
                name="loyalty.birthdate"
                label={
                  <Popover
                    list={{
                      title: 'Sua data de nascimento será usada para:',
                      items: ['Envio de mimos no dia do seu aniversário :)']
                    }}
                  >
                    Data de nascimento
                  </Popover>
                }
                rules={{
                  required: 'Insira sua data de nascimento',
                  validate: value => birthdateErrorMessage(value)
                }}
                control={control}
                isInvalid={Boolean(errors.loyalty?.birthdate)}
                validationHint={errors.loyalty?.birthdate?.message}
                format="##/##/####"
                keepFormatted
              />

              <Checkbox
                name="loyalty.optInPro"
                validationHint="Profissionais das áreas de engenharia, arquitetura, decoração, ou mesmo quem atua como pedreiro ou pedreira, etc tem vantagens únicas como carteira de clientes e acesso à eventos."
                onChange={handleIsProfessional}
                ref={register}
              >
                Sou profissional da construção civil
              </Checkbox>

              {isProfessional && (
                <SelectField
                  name="loyalty.occupation"
                  label="Qual sua profissão?"
                  options={[
                    {
                      value: 'engineer',
                      label: 'Engenheiro'
                    },
                    {
                      value: 'architect',
                      label: 'Arquiteto'
                    },
                    {
                      value: 'decorator',
                      label: 'Decorador'
                    },
                    {
                      value: 'mason',
                      label: 'Pedreiro'
                    },
                    {
                      value: 'joiner',
                      label: 'Marceneiro'
                    },
                    {
                      value: 'electrician',
                      label: 'Eletricista'
                    },
                    {
                      value: 'ceramist',
                      label: 'Ceramista'
                    },
                    {
                      value: 'plumber',
                      label: 'Encanador'
                    },
                    {
                      value: 'gardener',
                      label: 'Jardineiro'
                    },
                    {
                      value: 'painter',
                      label: 'Pintor'
                    },
                    {
                      value: 'blacksmith',
                      label: 'Serralheiro'
                    },
                    {
                      value: 'landscaper',
                      label: 'Paisagista'
                    },
                    {
                      value: 'constructor',
                      label: 'Construtor'
                    },
                    {
                      value: 'other',
                      label: 'Outro'
                    }
                  ]}
                  rules={{
                    required: 'Escolha sua profissão'
                  }}
                  control={control}
                  isInvalid={Boolean(errors.loyalty?.occupation)}
                  validationHint={errors.loyalty?.occupation?.message}
                />
              )}
            </Stack>
          )}

          <S.GeneralCheckbox
            ref={register}
            onClick={() =>
              setNewsLetterState(prevState =>
                generalNewsletterToggle(prevState)
              )
            }
            validationHint="Escolha em quais canais você prefere receber"
            checked={newsLetterState.general === 'checked'}
            isIndeterminate={newsLetterState.general === 'indeterminate'}
          >
            Receber comunicação da Leroy Merlin
          </S.GeneralCheckbox>

          <S.NewsletterOptionsWrapper>
            <Visible untilKilo>
              <Stack spacing="byte">{optInsCheckboxes}</Stack>
            </Visible>

            <Hidden untilKilo>
              <Inline spacing="giga" justify="left">
                {optInsCheckboxes}
              </Inline>
            </Hidden>
          </S.NewsletterOptionsWrapper>

          <Button isStretch type="submit" isLoading={isRequest}>
            Concordo e quero criar conta
          </Button>
        </Stack>
      </form>
    </Fragment>
  )
}

export default memo(FormPF)
